import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Box, Typography, Paper } from '@mui/material';
import AssistantAvatar from './AssistantAvatar';
import { AT } from './assistantStijl';

// Markdown via react-markdown (gesanitizeerd): de zelfgebouwde renderer escapede geen
// quotes in href="…" — attribuut-injectie op LLM-output was bewezen (P0 5 sep, FE-01).
const veiligeHref = (href) => {
  try { const u = new URL(String(href || ''), window.location.origin); return ['http:', 'https:', 'mailto:'].includes(u.protocol) ? u.href : undefined; }
  catch { return undefined; }
};
const mdComponents = {
  a: ({ href, children }) => <a href={veiligeHref(href)} target="_blank" rel="noopener noreferrer">{children}</a>,
  p: ({ children }) => <span style={{ display: 'block', marginBottom: 6 }}>{children}</span>,
  ul: ({ children }) => <ul style={{ margin: '4px 0', paddingLeft: 20 }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ margin: '4px 0', paddingLeft: 20 }}>{children}</ol>,
  code: ({ inline, children }) => (inline
    ? <code style={{ background: 'rgba(0,0,0,0.06)', padding: '1px 4px', borderRadius: 3 }}>{children}</code>
    : <pre style={{ background: 'rgba(0,0,0,0.06)', padding: 8, borderRadius: 4, overflowX: 'auto' }}><code>{children}</code></pre>),
};
function SimpleMarkdown({ children }) {
  return <ReactMarkdown components={mdComponents}>{String(children || '')}</ReactMarkdown>;
}

export default function AssistantMessage({ message }) {
  const isUser = message.role === 'user';
  const isStreaming = message.isStreaming;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        maxWidth: '100%',
      }}
    >
      {/* Assistant avatar */}
      {!isUser && (
        <AssistantAvatar sx={{ mr: 1, mt: 0.5 }} />
      )}

      <Paper
        elevation={0}
        sx={{
          px: 2,
          py: 1.5,
          maxWidth: '85%',
          borderRadius: isUser ? '1rem 1rem 0.25rem 1rem' : '1rem 1rem 1rem 0.25rem',
          // Zelfde palet als de rest van de site: slate voor de eigen vraag, rustig vlak voor het antwoord.
          bgcolor: isUser ? AT.primary : AT.surfaceLow,
          color: isUser ? '#f8f8ff' : AT.text,
        }}
      >
        {isUser ? (
          <Typography sx={{ fontSize: 14, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {message.content}
          </Typography>
        ) : (
          <Box
            sx={{
              fontSize: 14,
              lineHeight: 1.6,
              '& p': { m: 0, mb: 1, '&:last-child': { mb: 0 } },
              '& ul, & ol': { m: 0, mb: 1, pl: 2.5 },
              '& li': { mb: 0.25 },
              '& code': {
                fontSize: 12,
                bgcolor: 'rgba(0,0,0,0.06)',
                px: 0.5,
                py: 0.25,
                borderRadius: 0.5,
                fontFamily: 'monospace',
              },
              '& pre': {
                bgcolor: 'rgba(0,0,0,0.06)',
                p: 1.5,
                borderRadius: 1,
                overflow: 'auto',
                mb: 1,
                '& code': { bgcolor: 'transparent', p: 0 },
              },
              '& a': { color: AT.primary, textDecoration: 'underline' },
              '& strong': { fontWeight: 600 },
            }}
          >
            <SimpleMarkdown>{message.content || ''}</SimpleMarkdown>
            {isStreaming && (
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: 6,
                  height: 16,
                  bgcolor: AT.secondary,
                  ml: 0.5,
                  verticalAlign: 'text-bottom',
                  animation: 'blink 1s step-end infinite',
                }}
              />
            )}
          </Box>
        )}
      </Paper>

      {/* Blinking cursor animation */}
      {isStreaming && (
        <style>{`
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0; }
          }
        `}</style>
      )}
    </Box>
  );
}
